import { Box, Button, Card, Container, Skeleton, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DataGridCustom from 'src/components/data-grid-custom';
import HeaderBreadcrumbs from 'src/components/header-bread-crumbs/HeaderBreadcrumbs';
import Page from 'src/components/page/page';
import SegmentedTabs from 'src/components/segmented-tabs/segmented-tabs';
import useBenefitsList from './benefits-list.hook';
import Iconify from 'src/components/iconify';
import { paths } from 'src/routes/paths';
import BenefitsDrawerFilters from './components/benefits-drawer-filters';
import { BenefitsTableToolbar } from './components/benefits-table-toolbar';
import ReportsTab from './components/reports-tab';
import LoadingButton from '@mui/lab/LoadingButton';
import BenefitsBulkEditPanel from './components/benefits-bulk-edit-panel';
import { lazy, Suspense, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { HIGHLIGHT_FADE_IN_SX } from './components/benefits-highlights-motion';

const loadHighlights = () => import('./components/benefits-highlights-tab');
const BenefitsHighlightsTab = lazy(loadHighlights);

export default function BenefitsList() {
  const {
    benefitsList,
    benefitsRows,
    isBenefitsTableLoading,
    TABS,
    currentTab,
    gridSortModel,
    paginationModel,
    benefitsColumns,
    selectedRows,
    openFiltersDrawer,
    searchText,
    selectedFiltersCount,
    statusFilters,
    appliedDraftFilters,
    setSearchText,
    setPaginationModel,
    handleSortModelChange,
    handleRowClick,
    navigate,
    handleFilterButtonClick,
    onCloseFilters,
    onApplyFilters,
    handleStatusFiltersChange,
    onRowSelectionModelChange,
    onHeaderSelectionCheckboxChange,
    handleChangeTab,
    handleNewsConfigurationSaved,
    handleCopySelectedBenefit,
    onExport,
    isLoadingExport,
    canCreateBenefits,
    canUpdateBenefits,
    hasAnySelection,
    totalSelectionCount,
    isBulkEditMode,
    isBulkEditLocked,
    bulkEditItems,
    bulkEditStatuses,
    bulkEditMessages,
    bulkEditPhase,
    invalidBulkEditIds,
    isBulkEditPanelCollapsed,
    handleToggleBulkEditMode,
    handleCancelBulkEdit,
    handleRemoveBulkEditItem,
    handleStartBulkEdit,
    handleFinishBulkEdit,
    handleToggleBulkEditPanel,
  } = useBenefitsList();
  // Select the tab immediately; render its heavier panel as interruptible background work.
  const contentTab = useDeferredValue(currentTab);
  const [hasVisitedHighlights, setHasVisitedHighlights] = useState(false);
  const [hasVisitedList, setHasVisitedList] = useState(currentTab === 'benefits');

  useEffect(() => {
    if (contentTab === 'highlights') setHasVisitedHighlights(true);
    if (contentTab === 'benefits') setHasVisitedList(true);
  }, [contentTab]);

  const editedBenefitIds = useMemo(
    () => new Set(bulkEditItems.map((item) => item.id)),
    [bulkEditItems]
  );

  const renderBenefitsList = useMemo(
    () => (
      <DataGridCustom
        animatedEachRow
        customFilters={
          <BenefitsTableToolbar
            searchText={searchText}
            selectedFiltersCount={selectedFiltersCount}
            statusFilters={statusFilters}
            statusFiltersDisabled={isBenefitsTableLoading}
            handleClickFilters={handleFilterButtonClick}
            onStatusFiltersChange={handleStatusFiltersChange}
            setSearchText={setSearchText}
          />
        }
        excludeExportButton
        columns={benefitsColumns}
        rows={benefitsRows}
        totalRowsCount={benefitsList?.totalFiltered ?? (benefitsList?.total || 0)}
        filteredRowsCount={benefitsList?.totalFiltered ?? (benefitsList?.total || 0)}
        initialVisibleColumns={{}}
        sortModel={gridSortModel}
        paginationModel={paginationModel}
        isLoading={isBenefitsTableLoading}
        checkboxSelection={!isBulkEditMode}
        onRowClick={isBulkEditMode ? undefined : handleRowClick}
        onFilterButtonClick={handleFilterButtonClick}
        setSortModel={handleSortModelChange}
        setPaginationModel={setPaginationModel}
        rowSelectionModel={isBulkEditMode ? [] : selectedRows}
        onRowSelectionModelChange={isBulkEditMode ? undefined : onRowSelectionModelChange}
        onHeaderSelectionCheckboxChange={
          isBulkEditMode ? undefined : onHeaderSelectionCheckboxChange
        }
        getRowClassName={(params) =>
          editedBenefitIds.has(String(params.row.benefitId))
            ? 'benefits-bulk-edit-modified-row'
            : ''
        }
        height={550}
      />
    ),
    [
      benefitsColumns,
      editedBenefitIds,
      isBulkEditMode,
      benefitsRows,
      benefitsList,
      gridSortModel,
      paginationModel,
      isBenefitsTableLoading,
      handleSortModelChange,
      handleRowClick,
      handleFilterButtonClick,
      handleStatusFiltersChange,
      onHeaderSelectionCheckboxChange,
      onRowSelectionModelChange,
      searchText,
      selectedFiltersCount,
      selectedRows,
      setPaginationModel,
      setSearchText,
      statusFilters,
    ]
  );

  return (
    <Page title="Lista de Benefícios">
      <Container
        maxWidth="xl"
        aria-busy={currentTab !== contentTab}
        sx={{ cursor: 'auto', userSelect: 'text' }}
      >
        <HeaderBreadcrumbs
          heading="Benefícios"
          links={[]}
          sx={{ mb: 3 }}
          action={
            currentTab === 'benefits' && (
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{
                  mb: 2,
                  pl: 4,
                  flexWrap: 'wrap',
                }}
                spacing={2}
              >
                <Button
                  size="small"
                  variant={isBulkEditMode ? 'outlined' : 'contained'}
                  color={isBulkEditMode ? 'warning' : 'inherit'}
                  startIcon={
                    <Iconify
                      icon={isBulkEditMode ? 'mdi:pencil-off-outline' : 'mdi:table-edit'}
                      sx={{ height: 16, width: 16 }}
                    />
                  }
                  onClick={handleToggleBulkEditMode}
                  disabled={!canUpdateBenefits || isBulkEditLocked}
                  sx={{
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  {isBulkEditMode ? 'Modo de Visualização' : 'Modo Edição'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Iconify icon="lucide:copy-plus" sx={{ height: 16, width: 16 }} />}
                  onClick={handleCopySelectedBenefit}
                  disabled={isBulkEditMode || totalSelectionCount !== 1 || !canCreateBenefits}
                  sx={{
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  Copiar dados
                </Button>
                <LoadingButton
                  size="small"
                  variant="contained"
                  startIcon={<Iconify icon="prime:file-export" sx={{ height: 16, width: 16 }} />}
                  onClick={onExport}
                  loading={isLoadingExport}
                  disabled={isBulkEditMode || !hasAnySelection}
                  sx={{
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  Exportar relatório
                </LoadingButton>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Iconify icon="mdi:plus" sx={{ height: 16, width: 16 }} />}
                  onClick={() => navigate(paths.dashboard.benefitsDetails)}
                  disabled={isBulkEditMode || !canCreateBenefits}
                  sx={{
                    '& .MuiButton-startIcon': {
                      marginRight: 0.5,
                    },
                  }}
                >
                  Cadastrar Benefício
                </Button>
              </Stack>
            )
          }
        />
        <SegmentedTabs
          ariaLabel="Abas de benefícios"
          currentTab={currentTab}
          tabs={TABS}
          onChangeTab={handleChangeTab}
          disabled={isBulkEditMode}
          animationMode="css"
          deferContent
        />
        {(hasVisitedList || contentTab === 'benefits') && (
          <Box
            aria-hidden={contentTab !== 'benefits'}
            sx={{
              display: contentTab === 'benefits' ? 'block' : 'none',
            }}
          >
            <Card
              sx={(theme) => ({
                ...HIGHLIGHT_FADE_IN_SX,
                px: 2,
                pt: 1,
                pb: 0,
                boxShadow: 4,
                '& .benefits-bulk-edit-cell': {
                  bgcolor: alpha(theme.palette.grey[300], 0.1),
                },
                '& .benefits-bulk-edit-readonly-cell': {
                  color: 'text.disabled',
                  bgcolor: alpha(theme.palette.grey[300], 0.1),
                  opacity: 0.68,
                  pointerEvents: 'none',
                },
                '& .MuiDataGrid-row.benefits-bulk-edit-modified-row .MuiDataGrid-cell': {
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  animation: 'benefitsBulkEditRowHighlight 360ms ease-out',
                },
                '& .MuiDataGrid-row.benefits-bulk-edit-modified-row:hover .MuiDataGrid-cell': {
                  bgcolor: alpha(theme.palette.primary.main, 0.18),
                },
                '@keyframes benefitsBulkEditRowHighlight': {
                  from: {
                    backgroundColor: alpha(theme.palette.primary.main, 0.28),
                  },
                  to: {
                    backgroundColor: alpha(theme.palette.primary.main, 0.14),
                  },
                },
                '& .MuiDataGrid-footerContainer .MuiTablePagination-root': {
                  mr: {
                    xs: 0,
                    sm: bulkEditItems.length > 0 ? '312px' : 0,
                  },
                  transition: theme.transitions.create('margin-right', {
                    duration: theme.transitions.duration.shorter,
                  }),
                },
              })}
            >
              <Box height="100%">{renderBenefitsList}</Box>
            </Card>
          </Box>
        )}
        {contentTab === 'reports' && (
          <Card sx={{ ...HIGHLIGHT_FADE_IN_SX, p: 2, boxShadow: 4 }}>
            <ReportsTab />
          </Card>
        )}
        {(hasVisitedHighlights || contentTab === 'highlights') && (
          <Box
            aria-hidden={contentTab !== 'highlights'}
            sx={{
              display: contentTab === 'highlights' ? 'block' : 'none',
            }}
          >
            <Card
              sx={{
                ...HIGHLIGHT_FADE_IN_SX,
                p: 2,
                boxShadow: 4,
                overflow: 'visible',
              }}
            >
              <Suspense fallback={<Skeleton variant="rounded" height={400} animation={false} />}>
                <BenefitsHighlightsTab
                  canEdit={canUpdateBenefits}
                  active={currentTab === 'highlights'}
                  onNewsConfigurationSaved={handleNewsConfigurationSaved}
                />
              </Suspense>
            </Card>
          </Box>
        )}
      </Container>
      <BenefitsDrawerFilters
        onClose={onCloseFilters}
        isOpen={openFiltersDrawer}
        onApplyFilters={onApplyFilters}
        appliedFilters={appliedDraftFilters}
      />
      {bulkEditItems.length > 0 && (
        <BenefitsBulkEditPanel
          items={bulkEditItems}
          statuses={bulkEditStatuses}
          messages={bulkEditMessages}
          phase={bulkEditPhase}
          invalidItemIds={invalidBulkEditIds}
          collapsed={isBulkEditPanelCollapsed}
          onToggleCollapsed={handleToggleBulkEditPanel}
          onRemoveItem={handleRemoveBulkEditItem}
          onCancel={handleCancelBulkEdit}
          onSubmit={handleStartBulkEdit}
          onFinish={handleFinishBulkEdit}
        />
      )}
    </Page>
  );
}
